import Chart from 'chart.js/auto';
import { t, getLang, toggleLang, updatePageTranslations } from './i18n.js';

// Reduce animation duration (default is 1000ms -> 100ms)
Chart.defaults.animation.duration = 100;

// API Configuration
const API_BASE = '/api';
const TOKEN_KEY = 'auth_token';

// Global state
let allLoadedData = []; // All data including test data
let cbcData = []; // Filtered data based on showTestData
let currentView = 'overview';
let currentDataMode = localStorage.getItem('dataMode') || 'cbc'; // 'cbc' or 'bio'
let showTestData = localStorage.getItem('showTestData') === 'true'; // Default: false
let selectedDataIndex = -1; // -1 means latest, otherwise index into cbcData
let trendsChart = null;
let referenceChart = null;
let miniCharts = {};
let radarChart = null;
let multiaxisChart = null;
let currentAllViewType = 'grid';

// BIO tests list
const BIO_TESTS = [
  'alt', 'ast', 'bilirubin', 'calcium', 
  'alp', 'ggt', 'magnesium', 
  'potassium', 'sodium', 'uric_acid', 'creatinine'
];

// API Helper functions
function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function setAuthToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function apiRequest(endpoint, options = {}) {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers
  });
  
  return response;
}

async function apiLogin(username, password) {
  const response = await apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password })
  });
  
  const data = await response.json();
  
  if (response.ok && data.token) {
    setAuthToken(data.token);
    return { success: true };
  }
  
  return { success: false, error: data.error || 'Login failed' };
}

async function apiLogout() {
  try {
    await apiRequest('/auth/logout', { method: 'POST' });
  } catch (e) {
    // Ignore logout errors
  }
  clearAuthToken();
}

async function fetchCbcData(includeTest = false) {
  const response = await apiRequest(`/data?includeTest=${includeTest}`);
  
  if (response.status === 401) {
    // Token expired or invalid
    clearAuthToken();
    return { success: false, error: 'Session expired' };
  }
  
  if (!response.ok) {
    return { success: false, error: 'Failed to fetch data' };
  }
  
  const data = await response.json();
  return { success: true, data: data.results || [] };
}

// Check if token is valid (not expired)
function isTokenValid() {
  const token = getAuthToken();
  if (!token) return false;
  
  try {
    // Decode JWT payload (base64)
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Check expiry (exp is in seconds)
    return payload.exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
}

// Get user info from token
function getUserFromToken() {
  const token = getAuthToken();
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.sub,
      username: payload.username,
      role: payload.role || 'user'
    };
  } catch (e) {
    return null;
  }
}

// Check if current user is admin
function isAdmin() {
  const user = getUserFromToken();
  return user?.role === 'admin';
}

// Admin API functions
async function fetchUsers() {
  const response = await apiRequest('/admin/users');
  if (!response.ok) {
    const data = await response.json();
    return { success: false, error: data.error || 'Failed to fetch users' };
  }
  const data = await response.json();
  return { success: true, users: data.users || [] };
}

async function createUserApi(username, password, role) {
  const response = await apiRequest('/admin/users', {
    method: 'POST',
    body: JSON.stringify({ username, password, role })
  });
  const data = await response.json();
  if (!response.ok) {
    return { success: false, error: data.error || 'Failed to create user' };
  }
  return { success: true, userId: data.userId };
}

async function updateUserApi(id, updates) {
  const response = await apiRequest(`/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
  const data = await response.json();
  if (!response.ok) {
    return { success: false, error: data.error || 'Failed to update user' };
  }
  return { success: true };
}

async function deleteUserApi(id) {
  const response = await apiRequest(`/admin/users/${id}`, {
    method: 'DELETE'
  });
  const data = await response.json();
  if (!response.ok) {
    return { success: false, error: data.error || 'Failed to delete user' };
  }
  return { success: true };
}

// Authentication handling
function initAuth() {
  const overlay = document.getElementById('auth-overlay');
  const form = document.getElementById('auth-form');
  const errorEl = document.getElementById('auth-error');
  const logoutBtn = document.getElementById('logout-btn');
  const adminBtn = document.getElementById('admin-btn');
  
  // Always set up logout handler
  logoutBtn.addEventListener('click', async () => {
    await apiLogout();
    window.location.reload();
  });
  
  // Always set up form handler
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const username = document.getElementById('username').value;
    const password = document.getElementById('password').value;
    const submitBtn = form.querySelector('button[type="submit"]');
    
    // Disable button during login
    submitBtn.disabled = true;
    submitBtn.textContent = 'Logging in...';
    
    const result = await apiLogin(username, password);
    
    submitBtn.disabled = false;
    submitBtn.textContent = 'Login';
    
    if (result.success) {
      overlay.classList.add('hidden');
      errorEl.textContent = '';
      // Show admin button if user is admin
      updateAdminButtonVisibility();
      // Load data after successful auth
      loadDataAndRender();
    } else {
      errorEl.textContent = result.error || 'Invalid username or password';
      document.getElementById('password').value = '';
    }
  });
  
  // Check if already authenticated with valid token
  if (isTokenValid()) {
    overlay.classList.add('hidden');
    updateAdminButtonVisibility();
    return true;
  }
  
  // Clear invalid token
  clearAuthToken();
  
  // Show overlay
  overlay.classList.remove('hidden');
  return false;
}

// Update admin button visibility based on role
function updateAdminButtonVisibility() {
  const adminBtn = document.getElementById('admin-btn');
  if (isAdmin()) {
    adminBtn.style.display = 'flex';
  } else {
    adminBtn.style.display = 'none';
  }
}

async function loadDataAndRender() {
  await loadData();
  renderOverview();
  renderTrendsChart('rbc');
  renderReferenceChart(cbcData.length - 1);
  renderTable();
}

// Admin panel functions
function initAdminPanel() {
  const adminBtn = document.getElementById('admin-btn');
  const adminOverlay = document.getElementById('admin-overlay');
  const adminClose = document.getElementById('admin-close');
  const addUserForm = document.getElementById('add-user-form');
  const editUserForm = document.getElementById('edit-user-form');
  const editUserModal = document.getElementById('edit-user-modal');
  const editCancel = document.getElementById('edit-cancel');
  
  // Open admin panel
  adminBtn.addEventListener('click', async () => {
    adminOverlay.classList.remove('hidden');
    await loadAndRenderUsers();
  });
  
  // Close admin panel
  adminClose.addEventListener('click', () => {
    adminOverlay.classList.add('hidden');
  });
  
  // Close on overlay click
  adminOverlay.addEventListener('click', (e) => {
    if (e.target === adminOverlay) {
      adminOverlay.classList.add('hidden');
    }
  });
  
  // Add user form
  addUserForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const username = document.getElementById('new-username').value;
    const password = document.getElementById('new-password').value;
    const role = document.getElementById('new-role').value;
    const errorEl = document.getElementById('add-user-error');
    
    const result = await createUserApi(username, password, role);
    
    if (result.success) {
      errorEl.textContent = '';
      addUserForm.reset();
      await loadAndRenderUsers();
    } else {
      errorEl.textContent = result.error;
    }
  });
  
  // Edit user form
  editUserForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = document.getElementById('edit-user-id').value;
    const username = document.getElementById('edit-username').value;
    const password = document.getElementById('edit-password').value;
    const role = document.getElementById('edit-role').value;
    const errorEl = document.getElementById('edit-user-error');
    
    const updates = { username, role };
    if (password) {
      updates.password = password;
    }
    
    const result = await updateUserApi(id, updates);
    
    if (result.success) {
      errorEl.textContent = '';
      editUserModal.classList.add('hidden');
      await loadAndRenderUsers();
    } else {
      errorEl.textContent = result.error;
    }
  });
  
  // Cancel edit
  editCancel.addEventListener('click', () => {
    editUserModal.classList.add('hidden');
    document.getElementById('edit-user-error').textContent = '';
  });
}

async function loadAndRenderUsers() {
  const tbody = document.getElementById('users-tbody');
  const currentUser = getUserFromToken();
  
  const result = await fetchUsers();
  
  if (!result.success) {
    tbody.innerHTML = `<tr><td colspan="4" style="text-align: center; color: var(--danger);">${result.error}</td></tr>`;
    return;
  }
  
  if (result.users.length === 0) {
    tbody.innerHTML = '<tr><td colspan="4" style="text-align: center;">No users found</td></tr>';
    return;
  }
  
  tbody.innerHTML = result.users.map(user => {
    const isCurrentUser = currentUser && currentUser.id === user.id;
    const createdDate = user.created_at ? new Date(user.created_at).toLocaleDateString() : '-';
    
    return `
      <tr class="${isCurrentUser ? 'current-user' : ''}">
        <td>${escapeHtml(user.username)}${isCurrentUser ? ' (you)' : ''}</td>
        <td><span class="role-badge ${user.role}">${user.role}</span></td>
        <td>${createdDate}</td>
        <td class="actions">
          <button class="btn btn-icon" onclick="openEditUser(${user.id}, '${escapeHtml(user.username)}', '${user.role}')" title="Edit">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
          ${!isCurrentUser ? `
            <button class="btn btn-icon" onclick="confirmDeleteUser(${user.id}, '${escapeHtml(user.username)}')" title="Delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          ` : ''}
        </td>
      </tr>
    `;
  }).join('');
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// Global functions for onclick handlers
window.openEditUser = function(id, username, role) {
  document.getElementById('edit-user-id').value = id;
  document.getElementById('edit-username').value = username;
  document.getElementById('edit-password').value = '';
  document.getElementById('edit-role').value = role;
  document.getElementById('edit-user-error').textContent = '';
  document.getElementById('edit-user-modal').classList.remove('hidden');
};

window.confirmDeleteUser = async function(id, username) {
  if (confirm(`Are you sure you want to delete user "${username}"?`)) {
    const result = await deleteUserApi(id);
    if (result.success) {
      await loadAndRenderUsers();
    } else {
      alert(result.error);
    }
  }
};

// Initialize app
async function init() {
  initTheme();
  initLanguage();
  initNavigation();
  initDataMode();
  initTestDataToggle();
  initAdminPanel();
  
  const isAuthenticated = initAuth();
  
  if (isAuthenticated) {
    await loadDataAndRender();
  }
}

// Theme handling
function toggleTheme() {
  const current = document.documentElement.getAttribute('data-theme');
  const newTheme = current === 'light' ? 'dark' : 'light';
  document.documentElement.setAttribute('data-theme', newTheme);
  localStorage.setItem('theme', newTheme);
  
  // Update charts with new theme colors
  const activeChart = document.querySelector('.chart-btn.active')?.dataset.chart || 'rbc';
  if (activeChart === 'all') {
    renderAllCharts(currentAllViewType);
  } else if (trendsChart) {
    renderTrendsChart(activeChart);
  }
  if (referenceChart) {
    const dateSelect = document.getElementById('reference-date');
    renderReferenceChart(parseInt(dateSelect.value));
  }
}

function initTheme() {
  const savedTheme = localStorage.getItem('theme') || 'light';
  document.documentElement.setAttribute('data-theme', savedTheme);
  
  document.getElementById('theme-toggle').addEventListener('click', toggleTheme);
  document.getElementById('theme-toggle-login').addEventListener('click', toggleTheme);
}

// Language handling
function doToggleLang() {
  toggleLang();
  updateLangButtons();
  
  // Re-render views with new language
  renderOverview();
  const activeChart = document.querySelector('.chart-btn.active')?.dataset.chart || 'rbc';
  if (activeChart === 'all') {
    renderAllCharts(currentAllViewType);
  } else {
    renderTrendsChart(activeChart);
  }
  const dateSelect = document.getElementById('reference-date');
  renderReferenceChart(parseInt(dateSelect.value));
  renderTable();
}

function initLanguage() {
  updateLangButtons();
  updatePageTranslations();
  
  document.getElementById('lang-toggle').addEventListener('click', doToggleLang);
  document.getElementById('lang-toggle-login').addEventListener('click', doToggleLang);
}

function updateLangButtons() {
  const lang = getLang().toUpperCase();
  const label = document.querySelector('.lang-label');
  if (label) label.textContent = lang;
  const loginBtn = document.getElementById('lang-toggle-login');
  if (loginBtn) loginBtn.textContent = lang;
}

// Navigation
function initNavigation() {
  // View switching
  document.querySelectorAll('.nav-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const view = btn.dataset.view;
      switchView(view);
      
      // Update active button
      document.querySelectorAll('.nav-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      // Close mobile menu
      document.querySelector('.nav-links').classList.remove('active');
      document.getElementById('hamburger').classList.remove('active');
    });
  });
  
  // Mobile hamburger
  document.getElementById('hamburger').addEventListener('click', () => {
    document.querySelector('.nav-links').classList.toggle('active');
    document.getElementById('hamburger').classList.toggle('active');
  });
  
  // Trends chart buttons
  document.querySelectorAll('.chart-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.chart-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      const chartType = btn.dataset.chart;
      if (chartType === 'all') {
        showAllView(currentAllViewType);
      } else {
        hideAllView();
        renderTrendsChart(chartType);
      }
    });
  });
  
  // All view type buttons
  document.querySelectorAll('.view-type-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.view-type-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentAllViewType = btn.dataset.viewType;
      renderAllCharts(currentAllViewType);
    });
  });
}

// Show/hide all view containers
function showAllView(viewType) {
  document.getElementById('all-view-selector').style.display = 'flex';
  document.getElementById('single-chart-container').style.display = 'none';
  document.getElementById('insufficient-data').style.display = 'none';
  renderAllCharts(viewType);
}

function hideAllView() {
  document.getElementById('all-view-selector').style.display = 'none';
  document.getElementById('single-chart-container').style.display = 'block';
  document.getElementById('charts-grid').style.display = 'none';
  document.getElementById('radar-chart-container').style.display = 'none';
  document.getElementById('multiaxis-chart-container').style.display = 'none';
  document.getElementById('insufficient-data').style.display = 'none';
}

// Render all charts based on view type
function renderAllCharts(viewType) {
  const insufficientDataEl = document.getElementById('insufficient-data');
  
  // Hide all containers first
  document.getElementById('charts-grid').style.display = 'none';
  document.getElementById('radar-chart-container').style.display = 'none';
  document.getElementById('multiaxis-chart-container').style.display = 'none';
  insufficientDataEl.style.display = 'none';
  
  // Check for insufficient data in BIO mode
  if (currentDataMode === 'bio' && getBioDataCount() < 2) {
    insufficientDataEl.style.display = 'block';
    return;
  }
  
  switch (viewType) {
    case 'grid':
      document.getElementById('charts-grid').style.display = 'grid';
      renderGridCharts();
      break;
    case 'radar':
      document.getElementById('radar-chart-container').style.display = 'block';
      renderRadarChart();
      break;
    case 'multiaxis':
      document.getElementById('multiaxis-chart-container').style.display = 'block';
      renderMultiaxisChart();
      break;
  }
}

function switchView(view) {
  currentView = view;
  document.querySelectorAll('.view').forEach(v => v.classList.remove('active'));
  document.getElementById(`view-${view}`).classList.add('active');
}

// Data mode (CBC/BIO) handling
function initDataMode() {
  // Set initial active state
  document.querySelectorAll('.mode-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.mode === currentDataMode);
  });
  
  // Add click handlers
  document.querySelectorAll('.mode-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const newMode = btn.dataset.mode;
      if (newMode === currentDataMode) return;
      
      currentDataMode = newMode;
      localStorage.setItem('dataMode', newMode);
      
      // Update button states
      document.querySelectorAll('.mode-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      // Re-render all views
      renderAllViews();
    });
  });
}

// Test data toggle handling
function initTestDataToggle() {
  const toggleContainer = document.querySelector('.test-data-toggle');
  const toggle = document.getElementById('test-data-toggle');
  
  // Only show for admins
  if (!isAdmin()) {
    if (toggleContainer) toggleContainer.style.display = 'none';
    showTestData = false; // Force off for non-admins
    return;
  }
  
  // Set initial state
  toggle.checked = showTestData;
  
  // Add change handler
  toggle.addEventListener('change', () => {
    showTestData = toggle.checked;
    localStorage.setItem('showTestData', showTestData);
    
    // Re-filter data and re-render
    filterData();
    updateDateSelector();
    renderAllViews();
  });
}

// Filter data based on showTestData setting
function filterData() {
  if (showTestData) {
    cbcData = [...allLoadedData];
  } else {
    cbcData = allLoadedData.filter(d => !d.meta?.test);
  }
  
  // Sort by date
  cbcData.sort((a, b) => new Date(a.collection.date) - new Date(b.collection.date));
}

// Update date selector options
function updateDateSelector() {
  const dateSelect = document.getElementById('reference-date');
  dateSelect.innerHTML = '';
  
  cbcData.forEach((data, index) => {
    const option = document.createElement('option');
    option.value = index;
    option.textContent = `${formatDate(data.collection.date)} - ${data.collection.lab}`;
    dateSelect.appendChild(option);
  });
  
  dateSelect.value = cbcData.length - 1;
}

function renderAllViews() {
  renderOverview();
  
  const activeChart = document.querySelector('.chart-btn.active')?.dataset.chart || 'rbc';
  if (activeChart === 'all') {
    renderAllCharts(currentAllViewType);
  } else {
    renderTrendsChart(activeChart);
  }
  
  const dateSelect = document.getElementById('reference-date');
  renderReferenceChart(parseInt(dateSelect.value));
  renderTable();
}

// Helper to get data with BIO values count
function getBioDataCount() {
  return cbcData.filter(d => d.biochemistry && Object.keys(d.biochemistry).length > 0).length;
}

// Data loading
async function loadData() {
  // Always fetch all data, filtering happens client-side based on showTestData
  const result = await fetchCbcData(true); // Always include test data, filter locally
  
  if (!result.success) {
    console.error('Failed to load data:', result.error);
    if (result.error === 'Session expired') {
      // Show login overlay
      document.getElementById('auth-overlay').classList.remove('hidden');
    }
    allLoadedData = [];
    cbcData = [];
    return;
  }
  
  allLoadedData = result.data.sort((a, b) => {
    return new Date(a.collection.date) - new Date(b.collection.date);
  });
  
  // Filter data based on showTestData setting
  filterData();
  
  // Populate date selector
  updateDateSelector();
  
  // Add change event listener (only once)
  const dateSelect = document.getElementById('reference-date');
  if (!dateSelect.hasAttribute('data-listener-added')) {
    dateSelect.setAttribute('data-listener-added', 'true');
    dateSelect.addEventListener('change', (e) => {
      renderReferenceChart(parseInt(e.target.value));
    });
  }
}

// Utility functions
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(getLang() === 'ro' ? 'ro-RO' : 'en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function getStatus(value, reference) {
  if (!reference || reference.length !== 2) return 'normal';
  if (value < reference[0]) return 'low';
  if (value > reference[1]) return 'high';
  return 'normal';
}

function getThemeColors() {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  return {
    text: isDark ? '#f0f0f5' : '#1a1a2e',
    grid: isDark ? '#3a3a50' : '#e0e0e8',
    accent: isDark ? '#6366f1' : '#4361ee',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    purple: '#8b5cf6',
    pink: '#ec4899',
    background: isDark ? '#252540' : '#ffffff'
  };
}

// Get value from cbc data
function getValue(data, testKey) {
  const cbc = data.cbc;
  if (!cbc) return null;
  
  // Direct key
  if (cbc[testKey]) return cbc[testKey];
  
  // Check differential
  if (cbc.differential && cbc.differential[testKey]) {
    return cbc.differential[testKey];
  }
  
  return null;
}

// Get historical values for a test across all data points (with dates for proportional spacing)
function getHistoricalValues(testKey, type = 'cbc') {
  const results = [];
  cbcData.forEach(data => {
    const date = data.collection?.date;
    if (!date) return;
    
    let value = null;
    if (type === 'bio') {
      value = data.biochemistry?.[testKey]?.value ?? null;
    } else {
      // CBC - check direct key and differential
      const cbc = data.cbc;
      if (cbc) {
        if (cbc[testKey]?.value !== undefined) {
          value = cbc[testKey].value;
        } else if (cbc.differential?.[testKey]?.value !== undefined) {
          value = cbc.differential[testKey].value;
        }
      }
    }
    
    if (value !== null) {
      results.push({ value, date: new Date(date).getTime() });
    }
  });
  return results;
}

// Create SVG sparkline (data is array of {value, date} objects)
function createSparkline(data, reference, status, height = 56, selectedDate = null) {
  if (!data || data.length === 0) return '';
  
  const colors = getThemeColors();
  const safeColor = colors.success;
  const dangerColor = colors.danger;
  const fallbackColor = '#8bc34a'; // Yellow-er green when no reference
  
  // ViewBox dimensions - will scale to fit container width
  const width = 200;
  
  // Extract values for Y scaling
  const values = data.map(d => d.value);
  const hasReference = reference && reference.length === 2;
  
  // Calculate Y bounds from data
  let minVal = Math.min(...values);
  let maxVal = Math.max(...values);
  
  // Always include reference lines in the visible range
  if (hasReference) {
    minVal = Math.min(minVal, reference[0]);
    maxVal = Math.max(maxVal, reference[1]);
  }
  
  // Add 8% padding for breathing room
  const range = maxVal - minVal || 1;
  minVal -= range * 0.08;
  maxVal += range * 0.08;
  
  // Calculate time range for X scaling (proportional dates)
  const dates = data.map(d => d.date);
  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);
  const dateRange = maxDate - minDate;
  const isSinglePoint = data.length === 1;
  
  const paddingLeft = 4;
  const paddingTop = 8;
  const paddingBottom = 8;
  const paddingRight = 36; // Space for reference labels
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  
  // Scale helpers - center single point, otherwise proportional
  const scaleX = (date) => isSinglePoint 
    ? paddingLeft + chartWidth / 2 
    : paddingLeft + ((date - minDate) / dateRange) * chartWidth;
  const scaleY = (v) => paddingTop + chartHeight - ((v - minVal) / (maxVal - minVal)) * chartHeight;
  
  // Build polyline points (proportional to dates)
  const points = data.map(d => `${scaleX(d.date)},${scaleY(d.value)}`).join(' ');
  
  // Helper to check if value is in range
  const isInRange = (val) => hasReference && val >= reference[0] && val <= reference[1];
  
  // Build dots - color based on whether value is in reference range
  const dots = data.map(d => {
    const cx = scaleX(d.date);
    const cy = scaleY(d.value);
    const dotColor = hasReference ? (isInRange(d.value) ? safeColor : dangerColor) : fallbackColor;
    const isSelected = selectedDate && Math.abs(d.date - selectedDate) < 86400000; // within 1 day
    if (isSelected) {
      return `<circle cx="${cx}" cy="${cy}" r="7" fill="none" stroke="${dotColor}" stroke-width="2" opacity="0.4"/>
              <circle cx="${cx}" cy="${cy}" r="4" fill="${dotColor}"/>`;
    }
    return `<circle cx="${cx}" cy="${cy}" r="4" fill="${dotColor}"/>`;
  }).join('');
  
  // Reference zones, lines, masks and polylines
  let refZones = '';
  let refLines = '';
  let defs = '';
  let polylines = '';
  
  if (hasReference) {
    const yLow = scaleY(reference[0]);
    const yHigh = scaleY(reference[1]);
    const yTop = paddingTop;
    const yBottom = height - paddingBottom;
    
    const safeZoneColor = safeColor + '1A'; // 10% opacity green
    const dangerZoneColor = dangerColor + '1A'; // 10% opacity red
    
    // Background zones
    refZones = `
      <rect x="${paddingLeft}" y="${yTop}" width="${chartWidth}" height="${Math.max(0, yHigh - yTop)}" fill="${dangerZoneColor}"/>
      <rect x="${paddingLeft}" y="${yHigh}" width="${chartWidth}" height="${Math.max(0, yLow - yHigh)}" fill="${safeZoneColor}"/>
      <rect x="${paddingLeft}" y="${yLow}" width="${chartWidth}" height="${Math.max(0, yBottom - yLow)}" fill="${dangerZoneColor}"/>
    `;
    
    // Limit lines with labels
    const limitColor = colors.warning + '4D'; // 30% opacity warning color
    const labelColor = colors.text + '90'; // 56% opacity text color
    
    refLines = `
      <line x1="${paddingLeft}" y1="${yLow}" x2="${paddingLeft + chartWidth + 6}" y2="${yLow}" 
            stroke="${limitColor}" stroke-width="1" stroke-dasharray="3,2"/>
      <line x1="${paddingLeft}" y1="${yHigh}" x2="${paddingLeft + chartWidth + 6}" y2="${yHigh}" 
            stroke="${limitColor}" stroke-width="1" stroke-dasharray="3,2"/>
      <text x="${paddingLeft + chartWidth + 8}" y="${yLow}" font-size="13" fill="${labelColor}" text-anchor="start" dominant-baseline="middle">${reference[0]}</text>
      <text x="${paddingLeft + chartWidth + 8}" y="${yHigh}" font-size="13" fill="${labelColor}" text-anchor="start" dominant-baseline="middle">${reference[1]}</text>
    `;
    
    // Build line segments with colors based on reference thresholds
    const refLow = reference[0];
    const refHigh = reference[1];
    
    // Helper to get color for a value
    const getColorForValue = (val) => (val >= refLow && val <= refHigh) ? safeColor : dangerColor;
    
    // Helper to find intersection point between two values crossing a threshold
    const findIntersection = (x1, v1, x2, v2, threshold) => {
      const t = (threshold - v1) / (v2 - v1);
      return { x: x1 + t * (x2 - x1), y: scaleY(threshold), value: threshold };
    };
    
    // Build colored segments
    let segments = [];
    for (let i = 0; i < data.length - 1; i++) {
      const p1 = data[i];
      const p2 = data[i + 1];
      const x1 = scaleX(p1.date), y1 = scaleY(p1.value);
      const x2 = scaleX(p2.date), y2 = scaleY(p2.value);
      const v1 = p1.value, v2 = p2.value;
      
      // Find all threshold crossings in this segment
      let points = [{ x: x1, y: y1, value: v1 }];
      
      // Check crossing with refLow
      if ((v1 < refLow && v2 >= refLow) || (v1 >= refLow && v2 < refLow)) {
        points.push(findIntersection(x1, v1, x2, v2, refLow));
      }
      // Check crossing with refHigh
      if ((v1 <= refHigh && v2 > refHigh) || (v1 > refHigh && v2 <= refHigh)) {
        points.push(findIntersection(x1, v1, x2, v2, refHigh));
      }
      
      points.push({ x: x2, y: y2, value: v2 });
      
      // Sort points by x position
      points.sort((a, b) => a.x - b.x);
      
      // Create line segments between consecutive points
      for (let j = 0; j < points.length - 1; j++) {
        const midValue = (points[j].value + points[j + 1].value) / 2;
        const color = getColorForValue(midValue);
        segments.push(`<line x1="${points[j].x}" y1="${points[j].y}" x2="${points[j + 1].x}" y2="${points[j + 1].y}" stroke="${color}" stroke-width="1.5" stroke-linecap="round"/>`);
      }
    }
    
    polylines = segments.join('\n      ');
  } else {
    // No reference - single line with fallback color
    polylines = `
      <polyline points="${points}" fill="none" stroke="${fallbackColor}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    `;
  }
  
  return `
    <svg class="sparkline" viewBox="0 0 ${width} ${height}" overflow="visible">
      ${refLines}
      ${polylines}
      ${dots}
    </svg>
  `;
}

// Render History Timeline
function renderTimeline() {
  const container = document.getElementById('history-timeline');
  if (!container) return;
  
  // Get ALL data timestamps for the full timeline span
  const allTimestamps = cbcData.map(d => new Date(d.collection?.date).getTime()).filter(t => !isNaN(t));
  if (allTimestamps.length === 0) {
    container.innerHTML = '';
    return;
  }
  const globalMinTime = Math.min(...allTimestamps);
  const globalMaxTime = Math.max(...allTimestamps);
  const globalTimeRange = globalMaxTime - globalMinTime;
  
  // Get relevant data with their original indices (for dots)
  let relevantItems = [];
  cbcData.forEach((d, idx) => {
    if (currentDataMode === 'bio') {
      if (d.biochemistry && Object.keys(d.biochemistry).length > 0) {
        relevantItems.push({ data: d, index: idx });
      }
    } else {
      if (d.cbc) {
        relevantItems.push({ data: d, index: idx });
      }
    }
  });
  
  if (relevantItems.length === 0) {
    container.innerHTML = '';
    return;
  }
  
  // Default to latest relevant item if not set or out of range
  const lastIdx = relevantItems[relevantItems.length - 1].index;
  if (selectedDataIndex === -1 || !relevantItems.some(item => item.index === selectedDataIndex)) {
    selectedDataIndex = lastIdx;
  }
  
  // Build timeline data with positions based on GLOBAL time range
  const timelineData = relevantItems.map(({ data: d, index: idx }, i) => {
    const dateStr = d.collection?.date;
    if (!dateStr) return null;
    const isActive = idx === selectedDataIndex;
    const isTest = d.meta?.test === true;
    const date = new Date(dateStr);
    const formattedDate = date.toLocaleDateString(getLang() === 'ro' ? 'ro-RO' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    });
    
    // Calculate position percentage (0-100) based on global time range
    let position;
    if (globalTimeRange === 0) {
      position = 50; // Single point overall, center it
    } else {
      position = ((date.getTime() - globalMinTime) / globalTimeRange) * 100;
    }
    
    return { idx, isActive, isTest, formattedDate, position };
  }).filter(item => item);
  
  // Build timeline items HTML
  const timelineItemsHtml = timelineData.map(item => `
    <div class="timeline-item ${item.isActive ? 'active' : ''} ${item.isTest ? 'test-data' : ''}" data-index="${item.idx}" style="left: ${item.position}%">
      <div class="timeline-dot"></div>
      <div class="timeline-label">
        <span class="timeline-date">${item.formattedDate}</span>
      </div>
    </div>
  `).join('');
  
  // Build SVG line - full span from 0 to 100%
  const colors = getThemeColors();
  const lineSegments = `
    <line x1="0" y1="8" x2="100" y2="8" stroke="${colors.grid}" stroke-width="2"/>
  `;
  
  container.innerHTML = `
    <div class="timeline-track">
      <div class="timeline-hover-zone"></div>
      <svg class="timeline-line-svg" viewBox="0 0 100 16" preserveAspectRatio="none">
        ${lineSegments}
      </svg>
      <div class="timeline-indicator"></div>
      <div class="timeline-items">
        ${timelineItemsHtml}
      </div>
    </div>
  `;
  
  const track = container.querySelector('.timeline-track');
  const indicator = container.querySelector('.timeline-indicator');
  const items = container.querySelectorAll('.timeline-item');
  
  // Add click handlers
  items.forEach(item => {
    item.addEventListener('click', () => {
      const idx = parseInt(item.dataset.index, 10);
      if (idx !== selectedDataIndex) {
        selectedDataIndex = idx;
        renderOverview();
      }
    });
  });
  
  // Hover indicator logic
  let hoveredItem = null;
  const SNAP_THRESHOLD = 25;
  
  track.addEventListener('mousemove', (e) => {
    const trackRect = track.getBoundingClientRect();
    const mouseX = e.clientX - trackRect.left;
    
    // Find nearest item
    let nearestItem = null;
    let nearestDist = Infinity;
    let nearestX = 0;
    
    items.forEach(item => {
      const dot = item.querySelector('.timeline-dot');
      const dotRect = dot.getBoundingClientRect();
      const dotCenterX = dotRect.left + dotRect.width / 2 - trackRect.left;
      const dist = Math.abs(mouseX - dotCenterX);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestItem = item;
        nearestX = dotCenterX;
      }
    });
    
    // Snap to point if close, otherwise follow mouse
    const isClose = nearestDist < SNAP_THRESHOLD;
    const padding = 16;
    const clampedX = Math.max(padding, Math.min(mouseX, trackRect.width - padding));
    
    indicator.style.left = `${isClose ? nearestX : clampedX}px`;
    indicator.style.opacity = '1';
    indicator.classList.toggle('snapped', isClose);
    
    if (nearestItem !== hoveredItem) {
      if (hoveredItem) hoveredItem.classList.remove('hover');
      hoveredItem = nearestItem;
    }
    
    // Highlight if close
    if (nearestItem && isClose) {
      nearestItem.classList.add('hover');
    } else if (nearestItem) {
      nearestItem.classList.remove('hover');
    }
  });
  
  track.addEventListener('mouseleave', () => {
    if (hoveredItem) {
      hoveredItem.classList.remove('hover');
      hoveredItem = null;
    }
    indicator.style.opacity = '0';
  });
  
  // Click on track to select nearest
  track.addEventListener('click', (e) => {
    if (hoveredItem) {
      const idx = parseInt(hoveredItem.dataset.index, 10);
      if (idx !== selectedDataIndex) {
        selectedDataIndex = idx;
        renderOverview();
      }
    }
  });
}

// Render Overview
function renderOverview() {
  const container = document.getElementById('overview-cards');
  container.innerHTML = '';
  
  if (cbcData.length === 0) {
    container.innerHTML = '<p>Nu s-au gasit date.</p>';
    return;
  }
  
  // Render the timeline
  renderTimeline();
  
  if (currentDataMode === 'bio') {
    renderOverviewBio(container);
  } else {
    renderOverviewCbc(container);
  }
}

function renderOverviewCbc(container) {
  const selected = cbcData[selectedDataIndex];
  if (!selected || !selected.cbc) return;
  const hemo = selected.cbc;
  const selectedDate = selected.collection?.date ? new Date(selected.collection.date).getTime() : null;
  
  // Key tests to display
  const tests = ['rbc', 'hemoglobin', 'hematocrit', 'wbc', 'platelets', 'mcv', 'mch', 'rdw'];
  
  tests.forEach(key => {
    const testData = hemo[key];
    if (!testData) return;
    
    const status = getStatus(testData.value, testData.reference);
    const card = document.createElement('div');
    card.className = `card status-${status}`;
    
    const refText = testData.reference 
      ? `${testData.reference[0]} - ${testData.reference[1]}` 
      : '-';
    
    // Get historical values for sparkline
    const historicalValues = getHistoricalValues(key, 'cbc');
    const sparkline = createSparkline(historicalValues, testData.reference, status, 56, selectedDate);
    
    card.innerHTML = `
      <div class="card-header">
        <div class="card-label">${t(`tests.${key}`) || key}</div>
        <div class="card-unit">${testData.unit}</div>
      </div>
      <div class="card-value">${testData.value}</div>
      ${sparkline}
      <div class="card-reference">${t('table.reference')}: ${refText}</div>
    `;
    
    container.appendChild(card);
  });
  
  // Add differential if available
  const formula = hemo.differential;
  if (formula) {
    ['neutrophils', 'lymphocytes', 'monocytes', 'eosinophils', 'basophils'].forEach(key => {
      if (!formula[key]) return;
      
      const testData = formula[key];
      const ref = testData.reference;
      const status = getStatus(testData.value, ref);
      
      const card = document.createElement('div');
      card.className = `card status-${status}`;
      
      const refText = ref ? `${ref[0]} - ${ref[1]}` : '-';
      const percentText = testData.percent ? ` (${testData.percent}%)` : '';
      
      // Get historical values for sparkline
      const historicalValues = getHistoricalValues(key, 'cbc');
      const sparkline = createSparkline(historicalValues, ref, status, 56, selectedDate);
      
      card.innerHTML = `
        <div class="card-header">
          <div class="card-label">${t(`tests.${key}`) || key}</div>
          <div class="card-unit">${testData.unit}</div>
        </div>
        <div class="card-value">${testData.value}${percentText}</div>
        ${sparkline}
        <div class="card-reference">${t('table.reference')}: ${refText}</div>
      `;
      
      container.appendChild(card);
    });
  }
}

function renderOverviewBio(container) {
  const selected = cbcData[selectedDataIndex];
  if (!selected || !selected.biochemistry || Object.keys(selected.biochemistry).length === 0) {
    container.innerHTML = `<p class="no-data-message">${t('trends.insufficientData')}</p>`;
    return;
  }
  
  const bio = selected.biochemistry;
  const selectedDate = selected.collection?.date ? new Date(selected.collection.date).getTime() : null;
  
  BIO_TESTS.forEach(key => {
    const testData = bio[key];
    if (!testData) return;
    
    const status = getStatus(testData.value, testData.reference);
    const card = document.createElement('div');
    card.className = `card status-${status}`;
    
    const refText = testData.reference 
      ? `${testData.reference[0]} - ${testData.reference[1]}` 
      : '-';
    
    // Get historical values for sparkline
    const historicalValues = getHistoricalValues(key, 'bio');
    const sparkline = createSparkline(historicalValues, testData.reference, status, 56, selectedDate);
    
    card.innerHTML = `
      <div class="card-header">
        <div class="card-label">${t(`tests.${key}`) || key}</div>
        <div class="card-unit">${testData.unit}</div>
      </div>
      <div class="card-value">${testData.value}</div>
      ${sparkline}
      <div class="card-reference">${t('table.reference')}: ${refText}</div>
    `;
    
    container.appendChild(card);
  });
}

// Render Trends Chart
function renderTrendsChart(type) {
  const insufficientDataEl = document.getElementById('insufficient-data');
  const singleChartContainer = document.getElementById('single-chart-container');
  
  // Check if we're in BIO mode
  if (currentDataMode === 'bio') {
    const bioDataCount = getBioDataCount();
    if (bioDataCount < 2) {
      // Show insufficient data message
      insufficientDataEl.style.display = 'block';
      singleChartContainer.style.display = 'none';
      if (trendsChart) {
        trendsChart.destroy();
        trendsChart = null;
      }
      return;
    }
  }
  
  // Hide insufficient data message, show chart
  insufficientDataEl.style.display = 'none';
  singleChartContainer.style.display = 'block';
  
  const ctx = document.getElementById('trends-chart').getContext('2d');
  const colors = getThemeColors();
  
  if (trendsChart) {
    trendsChart.destroy();
  }
  
  const labels = cbcData.map(d => formatDate(d.collection.date));
  let datasets = [];
  let yAxisLabel = '';
  
  // BIO mode charts
  if (currentDataMode === 'bio') {
    return renderTrendsChartBio(type, ctx, colors, labels);
  }
  
  switch (type) {
    case 'rbc':
      datasets = [
        {
          label: t('tests.rbc'),
          data: cbcData.map(d => d.cbc?.rbc?.value),
          borderColor: colors.danger,
          backgroundColor: colors.danger + '20',
          tension: 0.3,
          fill: true
        },
        {
          label: t('tests.hemoglobin'),
          data: cbcData.map(d => d.cbc?.hemoglobin?.value),
          borderColor: colors.accent,
          backgroundColor: colors.accent + '20',
          tension: 0.3,
          fill: true,
          yAxisID: 'y1'
        }
      ];
      yAxisLabel = '10⁶/µL';
      break;
      
    case 'wbc':
      datasets = [
        {
          label: t('tests.wbc'),
          data: cbcData.map(d => d.cbc?.wbc?.value),
          borderColor: colors.accent,
          backgroundColor: colors.accent + '20',
          tension: 0.3,
          fill: true
        }
      ];
      
      // Add differential if available
      const hasFormula = cbcData.some(d => d.cbc?.differential);
      if (hasFormula) {
        const diffColors = [colors.success, colors.warning, '#8b5cf6', '#ec4899'];
        ['neutrophils', 'lymphocytes', 'monocytes', 'eosinophils'].forEach((key, i) => {
          datasets.push({
            label: t(`tests.${key}`),
            data: cbcData.map(d => d.cbc?.differential?.[key]?.value),
            borderColor: diffColors[i],
            backgroundColor: diffColors[i] + '20',
            tension: 0.3,
            fill: false
          });
        });
      }
      yAxisLabel = '10³/µL';
      break;
      
    case 'platelets':
      datasets = [{
        label: t('tests.platelets'),
        data: cbcData.map(d => d.cbc?.platelets?.value),
        borderColor: colors.warning,
        backgroundColor: colors.warning + '20',
        tension: 0.3,
        fill: true
      }];
      yAxisLabel = '10³/µL';
      break;
      
    case 'hemoglobin':
      datasets = [
        {
          label: t('tests.hemoglobin'),
          data: cbcData.map(d => d.cbc?.hemoglobin?.value),
          borderColor: colors.danger,
          backgroundColor: colors.danger + '20',
          tension: 0.3,
          fill: true
        },
        {
          label: t('tests.hematocrit'),
          data: cbcData.map(d => d.cbc?.hematocrit?.value),
          borderColor: colors.accent,
          backgroundColor: colors.accent + '20',
          tension: 0.3,
          fill: true,
          yAxisID: 'y1'
        }
      ];
      yAxisLabel = 'g/dL';
      break;
  }
  
  const hasSecondAxis = datasets.some(d => d.yAxisID === 'y1');
  
  trendsChart = new Chart(ctx, {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        legend: {
          labels: { color: colors.text }
        }
      },
      scales: {
        x: {
          ticks: { color: colors.text },
          grid: { color: colors.grid }
        },
        y: {
          position: 'left',
          ticks: { color: colors.text },
          grid: { color: colors.grid },
          title: {
            display: true,
            text: yAxisLabel,
            color: colors.text
          }
        },
        ...(hasSecondAxis && {
          y1: {
            position: 'right',
            ticks: { color: colors.text },
            grid: { display: false },
            title: {
              display: true,
              text: type === 'rbc' ? 'g/dL' : '%',
              color: colors.text
            }
          }
        })
      }
    }
  });
}

// Render Trends Chart for BIO mode
function renderTrendsChartBio(type, ctx, colors, labels) {
  // For BIO mode, we show all BIO tests in one chart since there's limited data
  const bioColors = [
    colors.danger, colors.accent, colors.success, colors.warning,
    colors.purple, colors.pink, '#14b8a6', '#f97316', '#8b5cf6', '#06b6d4', '#84cc16'
  ];
  
  const datasets = BIO_TESTS.map((testKey, index) => ({
    label: t(`tests.${testKey}`) || testKey,
    data: cbcData.map(d => d.biochemistry?.[testKey]?.value || null),
    borderColor: bioColors[index % bioColors.length],
    backgroundColor: bioColors[index % bioColors.length] + '20',
    tension: 0.3,
    fill: false,
    spanGaps: true
  }));
  
  trendsChart = new Chart(ctx, {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        legend: {
          labels: { color: colors.text }
        }
      },
      scales: {
        x: {
          ticks: { color: colors.text },
          grid: { color: colors.grid }
        },
        y: {
          ticks: { color: colors.text },
          grid: { color: colors.grid }
        }
      }
    }
  });
}

// Render Grid Charts (small multiples)
function renderGridCharts() {
  const colors = getThemeColors();
  const labels = cbcData.map(d => formatDate(d.collection.date));
  
  // Destroy existing mini charts
  Object.values(miniCharts).forEach(chart => chart?.destroy());
  miniCharts = {};
  
  const chartConfigs = {
    rbc: {
      label: t('tests.rbc'),
      data: cbcData.map(d => d.cbc?.rbc?.value),
      color: colors.danger,
      unit: '10⁶/µL'
    },
    wbc: {
      label: t('tests.wbc'),
      data: cbcData.map(d => d.cbc?.wbc?.value),
      color: colors.accent,
      unit: '10³/µL'
    },
    platelets: {
      label: t('tests.platelets'),
      data: cbcData.map(d => d.cbc?.platelets?.value),
      color: colors.warning,
      unit: '10³/µL'
    },
    hemoglobin: {
      label: t('tests.hemoglobin'),
      data: cbcData.map(d => d.cbc?.hemoglobin?.value),
      color: colors.success,
      unit: 'g/dL'
    }
  };
  
  Object.entries(chartConfigs).forEach(([key, config]) => {
    const canvas = document.getElementById(`mini-chart-${key}`);
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    miniCharts[key] = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: config.label,
          data: config.data,
          borderColor: config.color,
          backgroundColor: config.color + '20',
          tension: 0.3,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            ticks: { color: colors.text, font: { size: 10 } },
            grid: { color: colors.grid }
          },
          y: {
            ticks: { color: colors.text, font: { size: 10 } },
            grid: { color: colors.grid },
            title: {
              display: true,
              text: config.unit,
              color: colors.text,
              font: { size: 10 }
            }
          }
        }
      }
    });
  });
}

// Render Radar Chart
function renderRadarChart() {
  const ctx = document.getElementById('radar-chart').getContext('2d');
  const colors = getThemeColors();
  
  if (radarChart) {
    radarChart.destroy();
  }
  
  // Normalize values to percentage of reference range for comparison
  const metrics = ['rbc', 'hemoglobin', 'hematocrit', 'wbc', 'platelets', 'rdw'];
  const metricLabels = metrics.map(m => t(`tests.${m}`));
  
  const datasets = cbcData.map((data, index) => {
    const hemo = data.cbc;
    const normalizedValues = metrics.map(metric => {
      const testData = hemo[metric];
      if (!testData || !testData.reference) return 50;
      
      const ref = testData.reference;
      const value = testData.value;
      const min = ref[0];
      const max = ref[1];
      // Normalize to 0-100 where 50 is middle of range
      return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
    });
    
    const hue = (index * 120) % 360;
    const color = `hsl(${hue}, 70%, 50%)`;
    
    return {
      label: formatDate(data.collection.date),
      data: normalizedValues,
      borderColor: color,
      backgroundColor: color.replace('50%)', '50%, 0.2)'),
      pointBackgroundColor: color,
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: color
    };
  });
  
  radarChart = new Chart(ctx, {
    type: 'radar',
    data: {
      labels: metricLabels,
      datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: { color: colors.text }
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const dataIndex = ctx.datasetIndex;
              const metricIndex = ctx.dataIndex;
              const metric = metrics[metricIndex];
              const data = cbcData[dataIndex];
              const hemo = data.cbc;
              
              const testData = hemo[metric];
              if (!testData) return 'N/A';
              const ref = testData.reference;
              return `${ctx.dataset.label}: ${testData.value} ${testData.unit} (ref: ${ref?.[0]}-${ref?.[1]})`;
            }
          }
        }
      },
      scales: {
        r: {
          min: 0,
          max: 100,
          ticks: {
            stepSize: 25,
            color: colors.text,
            backdropColor: 'transparent',
            callback: (value) => {
              if (value === 0) return 'Min';
              if (value === 50) return 'Mid';
              if (value === 100) return 'Max';
              return '';
            }
          },
          grid: { color: colors.grid },
          angleLines: { color: colors.grid },
          pointLabels: { color: colors.text }
        }
      }
    }
  });
}

// Render Multi-axis Chart
function renderMultiaxisChart() {
  const ctx = document.getElementById('multiaxis-chart').getContext('2d');
  const colors = getThemeColors();
  
  if (multiaxisChart) {
    multiaxisChart.destroy();
  }
  
  const labels = cbcData.map(d => formatDate(d.collection.date));
  
  const datasets = [
    {
      label: t('tests.rbc') + ' (10⁶/µL)',
      data: cbcData.map(d => d.cbc?.rbc?.value),
      borderColor: colors.danger,
      backgroundColor: colors.danger + '20',
      yAxisID: 'y-rbc',
      tension: 0.3
    },
    {
      label: t('tests.hemoglobin') + ' (g/dL)',
      data: cbcData.map(d => d.cbc?.hemoglobin?.value),
      borderColor: colors.success,
      backgroundColor: colors.success + '20',
      yAxisID: 'y-hgb',
      tension: 0.3
    },
    {
      label: t('tests.wbc') + ' (10³/µL)',
      data: cbcData.map(d => d.cbc?.wbc?.value),
      borderColor: colors.accent,
      backgroundColor: colors.accent + '20',
      yAxisID: 'y-wbc',
      tension: 0.3
    },
    {
      label: t('tests.platelets') + ' (10³/µL)',
      data: cbcData.map(d => d.cbc?.platelets?.value),
      borderColor: colors.warning,
      backgroundColor: colors.warning + '20',
      yAxisID: 'y-plt',
      tension: 0.3
    }
  ];
  
  multiaxisChart = new Chart(ctx, {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          labels: { color: colors.text }
        }
      },
      scales: {
        x: {
          ticks: { color: colors.text },
          grid: { color: colors.grid }
        },
        'y-rbc': {
          type: 'linear',
          position: 'left',
          ticks: { color: colors.danger },
          grid: { display: false },
          title: {
            display: true,
            text: 'RBC',
            color: colors.danger
          }
        },
        'y-hgb': {
          type: 'linear',
          position: 'left',
          offset: true,
          ticks: { color: colors.success },
          grid: { display: false },
          title: {
            display: true,
            text: 'Hgb',
            color: colors.success
          }
        },
        'y-wbc': {
          type: 'linear',
          position: 'right',
          ticks: { color: colors.accent },
          grid: { display: false },
          title: {
            display: true,
            text: 'WBC',
            color: colors.accent
          }
        },
        'y-plt': {
          type: 'linear',
          position: 'right',
          offset: true,
          ticks: { color: colors.warning },
          grid: { color: colors.grid },
          title: {
            display: true,
            text: 'PLT',
            color: colors.warning
          }
        }
      }
    }
  });
}

// Render Reference Chart
function renderReferenceChart(dataIndex) {
  const ctx = document.getElementById('reference-chart').getContext('2d');
  const colors = getThemeColors();
  
  if (referenceChart) {
    referenceChart.destroy();
  }
  
  const data = cbcData[dataIndex];
  if (!data) return;
  
  // Check mode and render accordingly
  if (currentDataMode === 'bio') {
    return renderReferenceChartBio(dataIndex, ctx, colors);
  }
  
  const hemo = data.cbc;
  const tests = [];
  
  // Collect all tests with reference ranges
  const addTest = (key, testData) => {
    if (!testData || !testData.reference) return;
    const ref = testData.reference;
    const value = testData.value;
    const min = ref[0];
    const max = ref[1];
    const percentage = ((value - min) / (max - min)) * 100;
    
    tests.push({
      name: t(`tests.${key}`) || key,
      value,
      min,
      max,
      percentage: Math.max(0, Math.min(150, percentage)), // Cap at 150%
      status: getStatus(value, ref)
    });
  };
  
  // Add main tests
  ['rbc', 'hemoglobin', 'hematocrit', 'mcv', 'mch', 'mchc', 'rdw', 'platelets', 'wbc', 'mpv', 'pdw'].forEach(key => {
    if (hemo[key]) addTest(key, hemo[key]);
  });
  
  // Add differential
  if (hemo.differential) {
    ['neutrophils', 'lymphocytes', 'monocytes', 'eosinophils', 'basophils'].forEach(key => {
      const testData = hemo.differential[key];
      if (testData && testData.reference) {
        addTest(key, testData);
      }
    });
  }
  
  // Remove duplicates (prefer more descriptive names)
  const uniqueTests = [];
  const seenValues = new Set();
  tests.forEach(test => {
    const key = `${test.value}-${test.min}-${test.max}`;
    if (!seenValues.has(key)) {
      seenValues.add(key);
      uniqueTests.push(test);
    }
  });
  
  const labels = uniqueTests.map(t => t.name);
  const values = uniqueTests.map(t => t.percentage);
  const barColors = uniqueTests.map(t => {
    if (t.status === 'low') return colors.warning;
    if (t.status === 'high') return colors.danger;
    return colors.success;
  });
  
  referenceChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Pozitie in interval (%)',
        data: values,
        backgroundColor: barColors,
        borderRadius: 4
      }]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const test = uniqueTests[ctx.dataIndex];
              return `${test.value} (${test.min} - ${test.max})`;
            }
          }
        }
      },
      scales: {
        x: {
          min: 0,
          max: 150,
          ticks: { 
            color: colors.text,
            callback: (value) => {
              if (value === 0) return 'Min';
              if (value === 100) return 'Max';
              if (value === 50) return '50%';
              return '';
            }
          },
          grid: { 
            color: (ctx) => {
              if (ctx.tick.value === 0 || ctx.tick.value === 100) {
                return colors.text + '40';
              }
              return colors.grid;
            }
          }
        },
        y: {
          ticks: { color: colors.text },
          grid: { display: false }
        }
      }
    }
  });
  
  // Draw reference zone overlay
  const refZone = {
    id: 'refZone',
    beforeDraw: (chart) => {
      const { ctx, chartArea: { left, right, top, bottom }, scales: { x } } = chart;
      const zeroX = x.getPixelForValue(0);
      const hundredX = x.getPixelForValue(100);
      
      ctx.save();
      ctx.fillStyle = colors.success + '15';
      ctx.fillRect(zeroX, top, hundredX - zeroX, bottom - top);
      ctx.restore();
    }
  };
  
  referenceChart.options.plugins.refZone = refZone;
}

// Render Reference Chart for BIO mode
function renderReferenceChartBio(dataIndex, ctx, colors) {
  const data = cbcData[dataIndex];
  const bio = data?.biochemistry;
  
  const tests = [];
  
  if (bio) {
    BIO_TESTS.forEach(key => {
      const testData = bio[key];
      if (!testData || !testData.reference) return;
      
      const ref = testData.reference;
      const value = testData.value;
      const min = ref[0];
      const max = ref[1];
      const percentage = ((value - min) / (max - min)) * 100;
      
      tests.push({
        name: t(`tests.${key}`) || key,
        value,
        min,
        max,
        percentage: Math.max(0, Math.min(150, percentage)),
        status: getStatus(value, ref)
      });
    });
  }
  
  if (tests.length === 0) {
    // No bio data for this date
    referenceChart = new Chart(ctx, {
      type: 'bar',
      data: { labels: [], datasets: [] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: t('trends.insufficientData'),
            color: colors.text
          }
        }
      }
    });
    return;
  }
  
  const labels = tests.map(t => t.name);
  const values = tests.map(t => t.percentage);
  const barColors = tests.map(t => {
    if (t.status === 'low') return colors.warning;
    if (t.status === 'high') return colors.danger;
    return colors.success;
  });
  
  referenceChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Pozitie in interval (%)',
        data: values,
        backgroundColor: barColors,
        borderRadius: 4
      }]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const test = tests[ctx.dataIndex];
              return `${test.value} (${test.min} - ${test.max})`;
            }
          }
        }
      },
      scales: {
        x: {
          min: 0,
          max: 150,
          ticks: { 
            color: colors.text,
            callback: (value) => {
              if (value === 0) return 'Min';
              if (value === 100) return 'Max';
              if (value === 50) return '50%';
              return '';
            }
          },
          grid: { color: colors.grid }
        },
        y: {
          ticks: { color: colors.text },
          grid: { display: false }
        }
      }
    }
  });
}

// Render Table
function renderTable() {
  const thead = document.querySelector('#data-table thead');
  const tbody = document.querySelector('#data-table tbody');
  
  // Build header
  let headerHtml = `<tr><th>${t('table.test')}</th><th>${t('table.unit')}</th>`;
  cbcData.forEach(d => {
    headerHtml += `<th>${formatDate(d.collection.date)}</th>`;
  });
  headerHtml += `<th>${t('table.reference')}</th></tr>`;
  thead.innerHTML = headerHtml;
  
  if (currentDataMode === 'bio') {
    renderTableBio(tbody);
  } else {
    renderTableCbc(tbody);
  }
}

function renderTableCbc(tbody) {
  // Collect all unique tests
  const allTests = new Map();
  
  cbcData.forEach(data => {
    const hemo = data.cbc;
    if (!hemo) return;
    Object.keys(hemo).forEach(key => {
      if (key === 'differential' && hemo.differential) {
        Object.keys(hemo.differential).forEach(fKey => {
          allTests.set(fKey, hemo.differential[fKey]);
        });
      } else if (hemo[key]?.value !== undefined) {
        allTests.set(key, hemo[key]);
      }
    });
  });
  
  // Build rows
  let bodyHtml = '';
  const processedKeys = new Set();
  
  // Define order (English keys)
  const orderedKeys = [
    'rbc', 'hemoglobin', 'hematocrit', 'mcv', 'mch', 'mchc', 'rdw',
    'platelets', 'mpv', 'pdw',
    'wbc', 'neutrophils', 'lymphocytes', 'monocytes', 'eosinophils', 'basophils'
  ];
  
  orderedKeys.forEach(key => {
    if (!allTests.has(key) || processedKeys.has(key)) return;
    processedKeys.add(key);
    
    const sample = allTests.get(key);
    const ref = sample.reference;
    const refText = ref ? `${ref[0]} - ${ref[1]}` : '-';
    
    bodyHtml += `<tr><td>${t(`tests.${key}`) || key}</td><td>${sample.unit}</td>`;
    
    cbcData.forEach(data => {
      const hemo = data.cbc;
      if (!hemo) {
        bodyHtml += `<td>-</td>`;
        return;
      }
      const testData = hemo[key] || hemo.differential?.[key];
      
      if (testData) {
        const status = getStatus(testData.value, testData.reference);
        bodyHtml += `<td class="${status}">${testData.value}</td>`;
      } else {
        bodyHtml += `<td>-</td>`;
      }
    });
    
    bodyHtml += `<td>${refText}</td></tr>`;
  });
  
  tbody.innerHTML = bodyHtml;
}

function renderTableBio(tbody) {
  // Collect sample data for reference ranges and units
  const sampleBio = cbcData.find(d => d.biochemistry)?.biochemistry || {};
  
  let bodyHtml = '';
  
  BIO_TESTS.forEach(key => {
    const sample = sampleBio[key];
    if (!sample) return;
    
    const ref = sample.reference;
    const refText = ref ? `${ref[0]} - ${ref[1]}` : '-';
    
    bodyHtml += `<tr><td>${t(`tests.${key}`) || key}</td><td>${sample.unit}</td>`;
    
    cbcData.forEach(data => {
      const testData = data.biochemistry?.[key];
      
      if (testData) {
        const status = getStatus(testData.value, testData.reference);
        bodyHtml += `<td class="${status}">${testData.value}</td>`;
      } else {
        bodyHtml += `<td>-</td>`;
      }
    });
    
    bodyHtml += `<td>${refText}</td></tr>`;
  });
  
  tbody.innerHTML = bodyHtml;
}

// Start the app
init();
